import { Fragment, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Me, Screen } from "../auth/session";
import mark from "../assets/skakuza-mark.png";
import AccountMenu from "./AccountMenu";
import GlobalSearch from "./GlobalSearch";

interface Props {
  me: Me;
  children: ReactNode;
}

/** Addresses that belong to a nav entry without sitting under its own: a
 * section's other tabs, and the documents a section opens. */
const ALSO_UNDER: Record<string, readonly string[]> = {
  dashboard: ["/workflow"],
  ingest: ["/extraction"],
  sites: ["/exceptions"],
  routing: ["/schedule"],
  commercial: ["/quotations", "/purchase-orders", "/boq", "/customer-orders", "/invoices"],
};

function under(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function isCurrent(screen: Screen, path: string): boolean {
  return under(path, screen.href) || (ALSO_UNDER[screen.key] ?? []).some((p) => under(path, p));
}

/** The nav entries in runs, as `/me` groups them. */
function runs(screens: readonly Screen[]): Screen[][] {
  const out: Screen[][] = [];
  for (const screen of screens) {
    const last = out[out.length - 1];
    if (last !== undefined && last[0].group === screen.group) {
      last.push(screen);
    } else {
      out.push([screen]);
    }
  }
  return out;
}

/** The frame every screen sits in: the masthead across the window, then the
 * screen. The nav is what `/me` says this account may see, in the order and
 * the runs it gives -- the overview, the field work, the money -- and is never
 * widened here: a screen absent from `me.screens` has no link, and its route
 * guard on the server refuses it anyway.
 *
 * Only `place: "nav"` entries are drawn in the nav. A tab inside a section
 * (Exceptions in Sites; Users and Spares in Admin) is drawn by that section,
 * and the places you go rather than steps in the work (Admin, Help) hang off
 * the avatar. A screen that wants the full width -- a rail and a workspace --
 * renders its root with `page-bleed` and the column gives way to it. */
export default function Shell({ me, children }: Props) {
  const { pathname } = useLocation();
  const nav = me.screens.filter((screen) => screen.nav);

  return (
    <div className="app-frame">
      <header className="masthead">
        <Link to={me.landing} className="masthead-brand" aria-label="PRV Maintenance, home">
          <img src={mark} alt="" width={30} height={30} />
          <span>
            <strong>PRV Maintenance</strong>
            <small>Tender 371G/2022/23</small>
          </span>
        </Link>
        <nav className="masthead-nav" aria-label="Main">
          {runs(nav).map((run, index) => (
            <Fragment key={run[0].key}>
              {index === 0 ? null : <span className="masthead-sep" aria-hidden="true" />}
              {run.map((screen) => {
                const current = isCurrent(screen, pathname);
                return (
                  <Link
                    key={screen.key}
                    to={screen.href}
                    className={current ? "masthead-link current" : "masthead-link"}
                    aria-current={current ? "page" : undefined}
                  >
                    {screen.label}
                  </Link>
                );
              })}
            </Fragment>
          ))}
        </nav>
        <div className="masthead-end">
          {me.role === "office" ? <GlobalSearch /> : null}
          <AccountMenu me={me} />
        </div>
      </header>
      <main className="app-shell">{children}</main>
    </div>
  );
}
